/**
 * Charges held after a refund (gdn-4242), as the biller's decision needs
 * them.
 *
 * When a refund takes back money that paid a charge, the patient owes that
 * charge again, but nothing collects it — no statement, invoice or payment
 * request — until a biller chooses one of two answers: Bill patient or
 * Write off. Everything here is pure: reading the server's flags and
 * shaping the two requests.
 */

import type { UnifiedEncounterPaymentsWorkspaceResponse } from '@store/generated/billing';

import type {
  ChargeLinePreviewLine,
  ChargeLinePreviewResponse,
  ReleasedChargeLine,
  ReleaseRefundHoldRequest,
  WriteOffRequest,
} from '../../store';
import { formatCents } from '../v2/shared/formatCents';

export interface HeldCharge {
  chargeLineId: string;
  /** What the charge is: its description, else its code, else "Charge". */
  label: string;
  /** CPT or HCPCS code, when known. */
  code: string | null;
  /** What the patient owes on it again: the line's remaining amount. */
  amountCents: number;
}

export type WriteOffReason = WriteOffRequest['reason'];

/** The server's refusal for one held charge, kept with the charge's name so
 * it can still be read after a re-read drops the line. */
export interface HeldChargeRefusal {
  label: string;
  message: string;
}

/** The write-offs route's reasons, in the order the dialog offers them. */
export const WRITE_OFF_REASONS: ReadonlyArray<{ value: WriteOffReason; label: string }> = [
  { value: 'courtesy', label: 'Courtesy' },
  { value: 'hardship', label: 'Hardship' },
  { value: 'bad_debt', label: 'Bad debt' },
  { value: 'other', label: 'Other' },
];

const labelOf = (line: ChargeLinePreviewLine): string =>
  line.description?.trim() || (line.cpt_code ? `CPT ${line.cpt_code}` : 'Charge');

const toHeldCharge = (line: ChargeLinePreviewLine): HeldCharge => ({
  chargeLineId: line.charge_line_id,
  label: labelOf(line),
  code: line.cpt_code ?? null,
  amountCents: line.patient_due_cents,
});

/** The preview's lines the server marks held after a refund. */
export const heldChargesOf = (preview: ChargeLinePreviewResponse | undefined): HeldCharge[] =>
  (preview?.encounters ?? []).flatMap(encounter =>
    (encounter.lines ?? []).filter(line => line.held_after_refund).map(toHeldCharge),
  );

/**
 * Refusals whose charge is no longer listed — another biller answered it
 * first, say, and the re-read that followed dropped the line. They are
 * shown on the card rather than lost with their line.
 */
export const unlistedRefusalsOf = (
  refusals: Record<string, HeldChargeRefusal>,
  charges: HeldCharge[],
): Array<HeldChargeRefusal & { chargeLineId: string }> =>
  Object.entries(refusals)
    .filter(([id]) => !charges.some(charge => charge.chargeLineId === id))
    .map(([chargeLineId, refusal]) => ({ chargeLineId, ...refusal }));

/** Bill patient for one held charge. */
export const releaseRequestOf = (charge: HeldCharge): ReleaseRefundHoldRequest => ({
  charge_line_ids: [charge.chargeLineId],
});

/**
 * Write off one held charge for exactly what it owes. The server takes the
 * visit from the charge line, so the request names no encounter.
 */
export const writeOffRequestOf = (
  charge: HeldCharge,
  reason: WriteOffReason,
  note: string,
): WriteOffRequest => ({
  amount_cents: charge.amountCents,
  charge_line_ids: [charge.chargeLineId],
  encounter_id: null,
  note: note.trim() || null,
  reason,
});

type ReleasedStatus = ReleasedChargeLine['patient_due_status'];

/** What Bill patient did, in the biller's words, from the status it returned. */
export const releaseOutcomeText = (status: ReleasedStatus, subject: string): string => {
  const outcomes: Record<ReleasedStatus, string> = {
    paid: 'Paid from money already on this visit.',
    invoiced: `Billed to the ${subject} on the open invoice.`,
    confirmed: `Billed to the ${subject}. It goes on the next statement.`,
    estimated: `Billed to the ${subject} as an estimate to collect at the front desk.`,
    in_collections:
      'Returned to collections, where it was before the refund. It stays off statements.',
  };
  return outcomes[status];
};

export const writeOffOutcomeText = (charge: HeldCharge): string =>
  `Wrote off ${formatCents(charge.amountCents)}.`;

/**
 * Said when the server gave no reason. A timeout or a dropped connection
 * may still have gone through, so this never claims that nothing changed.
 */
export const UNKNOWN_OUTCOME_TEXT =
  "Couldn't confirm this went through. Reload to check the charge before trying again.";

type WorkspaceAction = NonNullable<UnifiedEncounterPaymentsWorkspaceResponse['actions']>[number];

/** The workspace's Record payment action while the held decision blocks it. */
export interface BlockedRecordPayment {
  label: string;
  reason: string;
}

/**
 * The payments workspace's Record payment, when the server has switched it
 * off with a reason ("Decide the held balance first"). The workspace shows
 * only its primary action, so the held card is where this one surfaces.
 */
export const blockedRecordPaymentOf = (
  actions: WorkspaceAction[] | undefined,
): BlockedRecordPayment | null => {
  const action = actions?.find(candidate => candidate.intent === 'record_payment');
  if (!action?.disabled || !action.disabled_reason) return null;
  return { label: action.label, reason: action.disabled_reason };
};
